<script setup>
import { computed, ref, shallowRef, watch } from "vue";
import moment from "moment";
import { useStore } from "vuex";
import UiFormDialog from "@/components/ui/UiFormDialog.vue";
import { apiRequest } from "@/api/http";
import { useAsyncAction } from "@/composables/useAsyncAction";
import { titleCase } from "@/utils/format";

// Files an academic or disciplinary concern about a student in one of the
// teacher's classes. It goes to the class adviser first (For resolution);
// either of them can resolve it or escalate it to the Prefect of Discipline.
// Other students involved, from any class, can be tagged on the report.
const props = defineProps({
  // Row from Class records: roomId, subjectId, subject_title, grade_level, room_section.
  classInfo: { type: Object, default: null },
});
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(["saved"]);

const store = useStore();
const { busy, run } = useAsyncAction();

const students = shallowRef([]);
// Everyone enrolled this school year, for "other students involved".
const directory = shallowRef([]);
const form = ref({});
const today = () => moment().format("YYYY-MM-DD");

const REPORT_TYPES = [
  { value: 1, title: "Academic concern" },
  { value: 2, title: "Disciplinary concern" },
];

watch(open, async (isOpen) => {
  if (!isOpen || !props.classInfo) return;
  form.value = {
    studentID: null,
    report_type: 2,
    report_date: today(),
    report_time: moment().format("HH:mm"),
    report_description: "",
    tag_students: [],
  };
  students.value = [];
  const list = await apiRequest(
    "get",
    `/rooms-section/getMyStudentAttendance/${store.state.user.id}/${store.getters.getFilterSelected}/${props.classInfo.roomId}`,
  ).catch(() => []);
  students.value = (Array.isArray(list) ? list : []).map((s) => ({
    value: s.id,
    title: titleCase(s.name) + (s.lrnNo ? ` · ${s.lrnNo}` : ""),
  }));
  const all = await apiRequest(
    "get",
    `/parent-records/students-directory/${store.getters.getFilterSelected}`,
  ).catch(() => []);
  directory.value = (Array.isArray(all) ? all : []).map((s) => {
    const middle = String(s.mname ?? "").trim();
    const name = titleCase(
      [
        s.lname && `${s.lname},`,
        s.fname,
        middle && middle.toLowerCase() !== "n/a" ? `${middle[0]}.` : "",
      ]
        .filter(Boolean)
        .join(" "),
    );
    return {
      value: s.id,
      // "Dela Cruz, Juan M. — Grade 8 · SAMPAGUITA · LRN 1234…"
      title: `${name} — ${
        [s.grade_level, s.section].filter(Boolean).join(" · ") || "No section"
      }${s.lrnNo ? ` · LRN ${s.lrnNo}` : ""}`,
    };
  });
});

// The student being reported can't also be tagged as involved.
const others = computed(() =>
  directory.value.filter((s) => s.value !== form.value.studentID),
);
watch(
  () => form.value.studentID,
  (id) => {
    if (form.value.tag_students?.includes(id)) {
      form.value = {
        ...form.value,
        tag_students: form.value.tag_students.filter((t) => t !== id),
      };
    }
  },
);

const notFuture = (v) =>
  !v || v <= today() || "The date cannot be in the future.";

const fields = computed(() => [
  {
    key: "studentID",
    label: "Student",
    type: "autocomplete",
    required: true,
    items: students.value,
    span: 2,
    placeholder: "Search by name or LRN",
  },
  {
    key: "report_type",
    label: "Type of concern",
    type: "select",
    required: true,
    items: REPORT_TYPES,
    span: 2,
  },
  {
    key: "report_date",
    label: "Date of incident",
    type: "date",
    required: true,
    max: today(),
    rules: [notFuture],
  },
  {
    key: "report_time",
    label: "Time of incident",
    type: "time",
    required: true,
  },
  {
    key: "report_description",
    label: "What happened",
    type: "textarea",
    required: true,
    span: 2,
    placeholder:
      "Describe the incident: what happened, where, and who was there.",
  },
  {
    key: "tag_students",
    label: "Other students involved",
    type: "autocomplete",
    multiple: true,
    items: others.value,
    span: 2,
    description:
      "Optional. Any student in the school; search by name or LRN. Each shows their grade, section and LRN.",
    placeholder: "Search students",
  },
]);

const description = computed(() =>
  [
    titleCase(props.classInfo?.subject_title),
    props.classInfo?.grade_level,
    props.classInfo?.room_section,
  ]
    .filter(Boolean)
    .join(" · "),
);

async function submit() {
  const c = props.classInfo;
  const body = {
    studentID: form.value.studentID,
    school_yearID: store.getters.getFilterSelected,
    subjectID: c.subjectId,
    roomID: c.roomId,
    grade_level: c.grade_level,
    report_type: form.value.report_type,
    report_date: form.value.report_date,
    report_time: form.value.report_time,
    report_description: form.value.report_description.trim(),
    tag_students: JSON.stringify(form.value.tag_students || []),
  };
  const ok = await run(
    () => apiRequest("post", "/parent-records/studentReport", body),
    {
      success: "Report filed. It's now for resolution with the class adviser.",
      error: "Could not file the report.",
    },
  );
  if (ok) {
    open.value = false;
    emit("saved");
  }
}
</script>

<template>
  <UiFormDialog
    v-model="open"
    v-model:values="form"
    title="File a report"
    :description="description"
    :fields="fields"
    layout="grid"
    size="lg"
    :saving="busy"
    submit-text="File report"
    @submit="submit"
  />
</template>
